import { nextTick, onBeforeUnmount, ref, shallowRef, type Ref } from 'vue'
import {
  CameraMode,
  createPotreeEngine,
  presets,
  type AssetPlugin,
  type EngineConfig,
  type GaussianSplatHandle,
  type GaussianSplatLoadRequest,
  type Plugin,
  type PotreeEngine,
} from '@sharefe/share3d-engine'
import {
  getDefaultPresetModelUrl,
  resolvePresetModelInputPath,
} from '../examples/presetModels'
import { resolveGaussianSplatInputPath } from '../utils/assetPath'
import {
  applyProjectInfoInitialCamera,
  applyProjectInfoModelMatrix,
  loadProjectInfoTransform,
} from '../utils/projectInfoCamera'

function getDefaultExampleModelUrl(): string {
  return getDefaultPresetModelUrl()
}

function isContainerReady(element: HTMLElement): boolean {
  return element.clientWidth > 0 && element.clientHeight > 0
}

async function waitForContainerLayout(element: HTMLElement): Promise<boolean> {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (isContainerReady(element)) {
      return true
    }

    await nextTick()
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        resolve()
      })
    })
  }

  return isContainerReady(element)
}

function getQueryString(): string {
  let queryString = window.location.search || ''

  if (!queryString) {
    const hash = window.location.hash || ''
    const queryIndex = hash.indexOf('?')
    if (queryIndex >= 0) {
      queryString = hash.slice(queryIndex)
    }
  }

  if (queryString.startsWith('??')) {
    return ''
  }
  if (queryString.startsWith('?')) {
    queryString = queryString.slice(1)
  }

  return queryString
}

export function getRawModelParamFromUrl(): string | null {
  const queryString = getQueryString()
  if (!queryString) {
    return null
  }

  const params = new URLSearchParams(queryString)
  let modelParam = params.get('models')
  if (!modelParam) {
    return null
  }

  if (/^\/[a-zA-Z]\//.test(modelParam)) {
    modelParam = `${modelParam[1]}:/${modelParam.slice(3)}`
  }

  return modelParam
}

export function resolveModelPath(modelPath: string): string {
  const presetUrl = resolvePresetModelInputPath(modelPath)
  if (presetUrl) {
    return presetUrl
  }

  return resolveGaussianSplatInputPath(modelPath)
}

export function getModelUrlFromQueryParams(): string | null {
  const modelParam = getRawModelParamFromUrl()
  if (!modelParam) {
    return null
  }

  return resolveModelPath(modelParam)
}

export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(2)}K`
  }

  return num.toString()
}

export interface Use3DGSOptions {
  plugins?: Plugin[]
  defaultModelUrl?: string
  autoLoadFromUrl?: boolean
  graphics?: EngineConfig['graphics']
  fov?: number
  background?: string
  fitOnLoad?: boolean
  renderMode?: EngineConfig['renderMode']
  /** 是否加载 SOG 高阶球谐数据；SH0 基础颜色始终保留 */
  loadSHN?: boolean
}

export interface Use3DGSLoadOptions {
  /** 是否加载 SOG 高阶球谐数据；未提供时沿用 use3DGS 的默认值 */
  loadSHN?: boolean
}

export interface GS3DStats {
  totalModels: number
  loadedIds: string[]
}

export function use3DGS(
  container: Ref<HTMLElement | null>,
  options: Use3DGSOptions = {},
) {
  const {
    plugins,
    defaultModelUrl = getDefaultExampleModelUrl(),
    autoLoadFromUrl = true,
    graphics,
    fov = 65,
    background = 'black',
    fitOnLoad = true,
    renderMode,
    loadSHN,
  } = options

  const engine = shallowRef<PotreeEngine | null>(null)
  const isLoading = ref(false)
  const modelInput = ref(getRawModelParamFromUrl() || '')
  const modelUrl = ref(getModelUrlFromQueryParams() || defaultModelUrl)
  const stats = ref<GS3DStats>({ totalModels: 0, loadedIds: [] })
  const loadedHandles = shallowRef<GaussianSplatHandle[]>([])

  const init = async () => {
    const containerElement = container.value
    if (!containerElement) {
      console.error('[use3DGS] container element is missing')
      return
    }

    const containerReady = await waitForContainerLayout(containerElement)
    if (!containerReady) {
      console.error('[use3DGS] container size is still 0 before init')
      return
    }

    const eng = await createPotreeEngine({
      container: containerElement,
      plugins: plugins ?? presets.pointCloudAndGSplat(),
      graphics,
      background,
      ...(graphics?.backend === 'webgpu' ? {} : { renderer: { antialias: false } }),
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      renderMode,
    })

    eng.camera.fov = fov
    eng.camera.setMode(CameraMode.Perspective)

    Reflect.set(window, 'engine', eng)
    engine.value = eng
  }

  const updateUrlHash = (path: string) => {
    const hash = window.location.hash || '#/'
    const hashPath = hash.split('?')[0]
    const query = new URLSearchParams(hash.split('?')[1] ?? '')

    if (path) {
      query.set('models', path)
      const newHash = `${hashPath}?${query.toString()}`
      window.history.replaceState(null, '', newHash)
      return
    }

    query.delete('models')
    const queryString = query.toString()
    window.history.replaceState(null, '', queryString ? `${hashPath}?${queryString}` : hashPath)
  }

  const updateStats = () => {
    stats.value = {
      totalModels: loadedHandles.value.length,
      loadedIds: loadedHandles.value.map((handle) => handle.id),
    }
  }

  const loadModel = async (inputPath?: string, loadOptions: Use3DGSLoadOptions = {}) => {
    if (!engine.value) {
      console.error('[use3DGS] engine is not initialized')
      return null
    }

    const rawPath = inputPath ?? modelInput.value
    if (!rawPath?.trim() || isLoading.value) {
      return null
    }

    if (loadedHandles.value.length > 0) {
      removeAllModels()
    }

    const trimmedPath = rawPath.trim()
    modelInput.value = trimmedPath
    updateUrlHash(trimmedPath)

    const targetUrl = resolveModelPath(trimmedPath)
    modelUrl.value = targetUrl

    isLoading.value = true
    try {
      const projectInfoTransformPromise = loadProjectInfoTransform(targetUrl)
      const resolvedLoadSHN = loadOptions.loadSHN ?? loadSHN
      const handle = await engine.value.loadGaussianSplat({
        url: targetUrl,
        name: `model-${Date.now()}`,
        options: resolvedLoadSHN === undefined ? undefined : { loadSHN: resolvedLoadSHN },
      })
      const projectInfoTransform = await projectInfoTransformPromise
      applyProjectInfoModelMatrix(handle, projectInfoTransform)

      console.log(`[use3DGS] model loaded: id=${handle.id}`)
      loadedHandles.value = [...loadedHandles.value, handle]
      updateStats()
      if (fitOnLoad) {
        engine.value.fitToAssets([handle.id])
      }
      applyProjectInfoInitialCamera(engine.value, projectInfoTransform)
      return handle
    } catch (error) {
      console.error('[use3DGS] load failed:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const removeAllModels = (clearUrl = false) => {
    const service = engine.value?.context.getService('GaussianSplatService') as
      | AssetPlugin<GaussianSplatLoadRequest, GaussianSplatHandle>
      | undefined

    for (const handle of loadedHandles.value) {
      service?.dispose(handle.id)
    }

    loadedHandles.value = []
    updateStats()

    if (clearUrl) {
      modelInput.value = ''
      updateUrlHash('')
    }
  }

  const resetCamera = () => {
    engine.value?.fitAll()
  }

  const setModelInput = (path: string) => {
    modelInput.value = path
  }

  const autoLoad = async () => {
    if (!autoLoadFromUrl) {
      return
    }

    const rawPath = getRawModelParamFromUrl()
    if (rawPath) {
      console.log(`[use3DGS] auto loading model from url: ${rawPath}`)
      modelInput.value = rawPath
      await loadModel()
      return
    }

    // URL 未指定模型时，默认自动加载预设模型
    if (defaultModelUrl) {
      console.log('[use3DGS] auto loading default model: ' + defaultModelUrl)
      modelInput.value = defaultModelUrl
      await loadModel()
    }
  }

  const dispose = () => {
    const currentEngine = engine.value
    if (currentEngine) {
      if (Reflect.get(window, 'engine') === currentEngine) {
        Reflect.deleteProperty(window, 'engine')
      }
      currentEngine.dispose()
    }
    engine.value = null
    loadedHandles.value = []
  }

  onBeforeUnmount(dispose)

  return {
    engine,
    isLoading,
    modelInput,
    modelUrl,
    stats,
    loadedHandles,
    init,
    loadModel,
    removeAllModels,
    resetCamera,
    setModelInput,
    autoLoad,
    updateStats,
    dispose,
    formatNumber,
    getModelUrlFromQueryParams,
    getRawModelParamFromUrl,
    resolveModelPath,
  }
}
